import { formatWorldClockPrompt } from '../../ui/worldRuntime/clock.js';
import { fallbackSummary } from '../fallbacks.js';
import { formatEvents, formatSchedule } from '../format.js';
import { buildInfluenceBlock, OPERATION_GUARDRAILS } from '../influence.js';
import type { PromptPurpose, PromptRequest, PromptSourceBundle } from '../types.js';
import { baseRequest, type CompilerSpec } from './base.js';
import { buildWorldContext } from '../filters.js';

function clockPromptLine(source: PromptSourceBundle, schedule: PromptSourceBundle['schedule']): string {
  const clock = formatWorldClockPrompt(source.clock.inWorldIso, schedule, source.location.region);
  return `CLOCK: ${clock}. This is the only year, date, and time.`;
}

function spec(purpose: Extract<PromptPurpose, 'location_summary' | 'missed_activity'>): CompilerSpec {
  return {
    purpose,
    maxTier: 3,
    allowedTiers: [1, 3],
    budget: { inputTokens: 1200, outputTokens: 400 },
    provider: 'deepseek',
  };
}

export function compileLocationSummary(
  source: PromptSourceBundle,
  persistedEventIds: string[],
): PromptRequest {
  const world = buildWorldContext(source);
  const events = source.events.filter((e) => persistedEventIds.includes(e.eventId));
  const influence = buildInfluenceBlock('location_summary', world);
  const closedNote =
    source.operatingStatus === 'closed'
      ? 'Location closed — summarize stored activity only; no new world activity.'
      : '';
  const header = [
    'PURPOSE: location_summary',
    OPERATION_GUARDRAILS.location_summary,
    closedNote,
    influence.text,
  ]
    .filter(Boolean)
    .join('\n');

  const tier1 = [
    clockPromptLine(source, world.schedule),
    `SCHEDULE: ${formatSchedule(world.schedule)}`,
    `OPERATING_STATUS: ${source.operatingStatus}`,
  ].join('\n');

  const tier3 = events.length
    ? `PERSISTED_EVENTS:\n${formatEvents(events)}`
    : 'PERSISTED_EVENTS: (none)';

  return baseRequest(
    source,
    spec('location_summary'),
    { tier0: '', tier1, tier3 },
    [],
    persistedEventIds,
    source.summaries.map((s) => s.summaryId),
    header,
    {
      local: () => fallbackSummary(persistedEventIds, source.events),
      retryOnValidationError: true,
      noKeyUsesLocal: true,
    },
  );
}

export function compileMissedActivity(
  source: PromptSourceBundle,
  persistedEventIds: string[],
  priorSummaryHash?: string,
): PromptRequest {
  const world = buildWorldContext(source);
  const events = source.events.filter((e) => persistedEventIds.includes(e.eventId));
  const influence = buildInfluenceBlock('missed_activity', world);
  const closedGuard =
    source.operatingStatus === 'closed'
      ? 'Location closed — summarize stored activity only; do NOT generate new world activity.'
      : '';

  const header = [
    'PURPOSE: missed_activity',
    OPERATION_GUARDRAILS.missed_activity,
    closedGuard,
    `Prior summary hash: ${priorSummaryHash ?? '(none)'}`,
    influence.text,
  ]
    .filter(Boolean)
    .join('\n');

  const tier1 = [
    clockPromptLine(source, world.schedule),
    `SCHEDULE: ${formatSchedule(world.schedule)}`,
    `OPERATING_STATUS: ${source.operatingStatus}`,
    world.schedule.overnight ? 'OVERNIGHT_SCHEDULE: true' : '',
  ]
    .filter(Boolean)
    .join('\n');

  const tier3 = events.length
    ? `MISSED_EVENTS:\n${formatEvents(events)}`
    : 'MISSED_EVENTS: (none)';

  return baseRequest(
    source,
    spec('missed_activity'),
    { tier0: '', tier1, tier3 },
    [],
    persistedEventIds,
    source.summaries.map((s) => s.summaryId),
    header,
    {
      local: () => fallbackSummary(persistedEventIds, source.events),
      retryOnValidationError: true,
      noKeyUsesLocal: true,
    },
  );
}

export const summaryMaxTier = 3;
